/**
 * SubscriptionsPage — VEN-06: Facturas Recurrentes / Suscripciones
 */
import { ordenControlado } from '@/components/ds/ordenControlado'
import { sortStateToParam, type SortState } from '@/components/SortableHeader'
import { useState } from 'react'
import { fmtDate } from '@/lib/utils'
import { formatMoneyForTenant, useTenantLocale } from '@/lib/locale'
import { RefreshCw, Plus, Loader2, Pause, Play, XCircle, Pencil, Zap } from 'lucide-react'
import {
  useRunNowRecurringInvoice,
  useRecurringInvoices,
  usePauseRecurringInvoice,
  useResumeRecurringInvoice,
  useCancelRecurringInvoice,
  type RecurringInvoice,
} from '../hooks/useSales'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import { PageHeader } from '@/components/HelpPanel'
import { HELP } from '@/data/helpContent'
import { SubscriptionModal } from './SubscriptionModal'
import { KebabMenu } from '@/components/KebabMenu'
import { DataTable } from '@/components/ds/DataTable'
import { Pagination } from '@/components/ds/Pagination'

type SubStatus = 'active' | 'paused' | 'cancelled'

const STATUS_CONFIG: Record<SubStatus, { label: string; cls: string; dotCls: string }> = {
  active:    { label: 'Activa',    cls: 'bg-success-surface text-success-content', dotCls: 'bg-emerald-400' },
  paused:    { label: 'Pausada',   cls: 'bg-warning-surface text-warning-content',    dotCls: 'bg-warning-solid' },
  cancelled: { label: 'Cancelada', cls: 'bg-surface-muted text-content-muted',   dotCls: 'bg-content-subtle' },
}

const FREQ_LABELS: Record<string, string> = {
  weekly: 'Semanal',
  monthly: 'Mensual',
  quarterly: 'Trimestral',
  annual: 'Anual',
}

const GENERATE_AS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  auto_emit: 'Emisión Automática',
}

function StatusBadge({ status }: { status: SubStatus }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.active
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.cls}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotCls}`} />
      {cfg.label}
    </span>
  )
}

function fmt(value: string | undefined) {
  if (!value) return '—'
  return formatMoneyForTenant(value)
}

export function SubscriptionsPage() {
  const locale = useTenantLocale()
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  // El orden va al SERVIDOR (`?ordering=`): esta lista la pagina el backend,
  // y ordenar en el navegador ordenaría solo la página visible.
  const [orden, setOrden] = useState<SortState | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingSub, setEditingSub] = useState<RecurringInvoice | null>(null)

  const { data, isLoading } = useRecurringInvoices({ ordering: sortStateToParam(orden),
    status: statusFilter || undefined,
    page,
    page_size: 25,
  })

  const pause = usePauseRecurringInvoice()
  const resume = useResumeRecurringInvoice()
  const runNow = useRunNowRecurringInvoice()
  const cancel = useCancelRecurringInvoice()
  const { confirm: confirmAction, modalProps } = useConfirmModal()

  const handleEdit = (sub: RecurringInvoice) => {
    setEditingSub(sub)
    setModalOpen(true)
  }

  const handleNewSub = () => {
    setEditingSub(null)
    setModalOpen(true)
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        title="Suscripciones"
        subtitle={`${data?.count ?? 0} factura${data?.count !== 1 ? 's' : ''} recurrente${data?.count !== 1 ? 's' : ''}`}
        help={HELP['sales.subscriptions']}
      >
        <button
          onClick={handleNewSub}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover transition-colors">
          <Plus size={15} />Nueva Suscripción
        </button>
      </PageHeader>

      {/* Filtros */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {(['', 'active', 'paused', 'cancelled'] as const).map(s => (
          <button key={s} onClick={() => { setStatusFilter(s); setPage(1) }}
            className={`px-3 py-1.5 text-xs rounded-lg border transition-colors
              ${statusFilter === s
                ? 'bg-primary text-on-primary border-primary'
                : 'border-divider text-content-muted hover:bg-soft'}`}>
            {s ? (STATUS_CONFIG[s as SubStatus]?.label ?? s) : 'Todas'}
          </button>
        ))}
      </div>

      {/* Tabla */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider overflow-x-auto shadow-card">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-content-subtle">
            <Loader2 size={20} className="animate-spin mr-2" />Cargando...
          </div>
        ) : !data?.subscriptions.length ? (
          <div className="text-center py-16">
            <RefreshCw size={32} className="mx-auto text-content-subtle mb-3" />
            <p className="text-content-muted text-sm font-medium">Sin suscripciones configuradas</p>
            <p className="text-content-subtle text-xs mt-1">Crea una suscripción para generar facturas recurrentes automáticamente.</p>
            <button
              onClick={handleNewSub}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm text-primary border border-primary-200 rounded-lg hover:bg-primary-50">
              <Plus size={14} />Crear primera suscripción
            </button>
          </div>
        ) : (
          <DataTable<typeof data.subscriptions[number]>
            {...ordenControlado(orden, s => { setOrden(s); setPage(1) })}
            columns={[
              { key: 'customer_name', header: 'Cliente', sortable: true,
                render: sub => (
                  <div className="text-sm font-medium text-content max-w-[160px] truncate">
                    {sub.customer_name}
                  </div>
                ) },
              { key: 'document_type', header: 'Tipo', sortable: true,
                render: sub => <span className="text-content-muted">{sub.document_type}</span> },
              { key: 'frequency', header: 'Frecuencia', sortable: true,
                sortValue: sub => FREQ_LABELS[sub.frequency] ?? sub.frequency,
                render: sub => (
                  <span className="text-content-muted">{FREQ_LABELS[sub.frequency] ?? sub.frequency}</span>
                ) },
              { key: 'next_generation_date', header: 'Próxima Gen.', sortable: true,
                render: sub => (
                  <span className="text-content-muted">{fmtDate(sub.next_generation_date, locale)}</span>
                ) },
              { key: 'generate_as', header: 'Modo', sortable: true,
                sortValue: sub => GENERATE_AS_LABELS[sub.generate_as] ?? sub.generate_as,
                render: sub => (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                    sub.generate_as === 'auto_emit'
                      ? 'bg-primary-50 text-primary-hover'
                      : 'bg-surface-muted text-content-muted'
                  }`}>
                    {GENERATE_AS_LABELS[sub.generate_as] ?? sub.generate_as}
                  </span>
                ) },
              { key: 'estimated_total', header: 'Total Est.', numeric: true,
                sortValue: sub => Number(sub.estimated_total),
                render: sub => <span className="font-medium text-content">{fmt(sub.estimated_total)}</span> },
              { key: 'status', header: 'Estado', sortable: true,
                render: sub => <StatusBadge status={sub.status} /> },
              { key: 'acciones', header: 'Acciones', width: 340,
                render: sub => (
                  <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                    {(sub.status === 'active' || sub.status === 'paused') && (
                      <button
                        onClick={() => handleEdit(sub)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-content-muted border border-divider rounded-lg hover:bg-soft">
                        <Pencil size={10} />Editar
                      </button>
                    )}
                    {sub.status === 'active' && (
                      <button
                        onClick={() => {
                          confirmAction({
                            message: `Esto generará un documento ahora a partir de la suscripción de ${sub.customer_name}, sin esperar al ciclo automático. ¿Continuar?`,
                            onConfirm: () => {
                              runNow.mutate(sub.id, {
                                onSuccess: (data) => {
                                  alert(`Documento generado (${data.document_status}). Próximo ciclo: ${data.next_generation_date || 'sin fecha'}`)
                                },
                                onError: (err: any) => {
                                  alert(err?.response?.data?.error?.message || 'No se pudo generar el documento.')
                                },
                              })
                            },
                          })
                        }}
                        disabled={runNow.isPending}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-primary border border-primary/40 rounded-lg hover:bg-primary/10 disabled:opacity-60">
                        <Zap size={10} />Generar ahora
                      </button>
                    )}
                    {sub.status === 'active' && (
                      <button
                        onClick={() => pause.mutate(sub.id)}
                        disabled={pause.isPending}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-warning-content border border-warning-border rounded-lg hover:bg-warning-surface disabled:opacity-60">
                        <Pause size={10} />Pausar
                      </button>
                    )}
                    {sub.status === 'paused' && (
                      <button
                        onClick={() => resume.mutate(sub.id)}
                        disabled={resume.isPending}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs text-success-content border border-success-border rounded-lg hover:bg-success-surface disabled:opacity-60">
                        <Play size={10} />Reanudar
                      </button>
                    )}
                    <KebabMenu items={[
                      { label: 'Cancelar', icon: <XCircle size={14} />, onClick: () => { confirmAction({ message: '¿Cancelar esta suscripción?', variant: 'danger', onConfirm: () => cancel.mutate(sub.id) }) }, hidden: sub.status === 'cancelled', className: 'text-danger-content-soft hover:bg-danger-surface', disabled: cancel.isPending },
                    ]} />
                  </div>
                ) },
            ]}
            rows={data.subscriptions}
            rowKey={sub => sub.id}
            minWidth={1280}
            surface={false}
            onRowClick={sub => handleEdit(sub)}
            caption="Suscripciones que generan documentos recurrentes"
          />
        )}
      </div>

      {/* Paginación */}
      {data && data.pages > 1 && (
        <Pagination
          page={page}
          pageSize={25}
          total={data?.count ?? 0}
          onPageChange={setPage}
          locale={locale}
        />
      )}

      {/* Modal */}
      {modalOpen && (
        <SubscriptionModal
          subscription={editingSub}
          onClose={() => { setModalOpen(false); setEditingSub(null) }}
        />
      )}

      <ConfirmModal {...modalProps} />
    </div>
  )
}
